// src/components/admin/PromptManagement.jsx
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  Tabs,
  Tab,
  Table,
  Modal,
  Button,
  Badge,
  Spinner,
  Row,
  Col,
  FloatingLabel,
  Form,
  Alert,
  InputGroup,
} from "react-bootstrap";
import { getToken } from "../../utilities/authHelper";
import TestPromptModal from "./TestPromptModal";
import ComparePromptVersions from "./ComparePromptVersions";
import EditPromptModal from "./EditPromptModal";

/* ───────────────────────────── API ───────────────────────────── */
const API = () => {
  const base = process.env.REACT_APP_API_URL;
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${getToken()}`,
  };
  return {
    getAll: async () => {
      const r = await fetch(`${base}/prompts/all`, { headers });
      const j = await r.json();
      return j.results || [];
    },
    filter: async (payload) => {
      const r = await fetch(`${base}/prompts/filter`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      const j = await r.json();
      return j.results || [];
    },
    activate: async (name, version) => {
      const u = new URL(`${base}/prompts/activate`);
      u.searchParams.set("prompt_name", name);
      u.searchParams.set("version", String(version));
      const r = await fetch(u.toString(), { headers });
      if (!r.ok) throw new Error("Activate failed");
      return (await r.json()).results;
    },
    create: async (payload) => {
      // used for both: create-new-prompt and create-new-version
      const r = await fetch(`${base}/prompts/create`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error("Create failed");
      return (await r.json()).results;
    },
    update: async (promptId, payload) => {
      const r = await fetch(`${base}/prompts/update/${promptId}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error("Update failed");
      return (await r.json()).results;
    },
    deleteVersion: async (promptId) => {
      const r = await fetch(`${base}/prompts/delete/${promptId}`, {
        method: "DELETE",
        headers,
      });
      if (!r.ok) throw new Error("Delete failed");
      return (await r.json()).results;
    },
  };
};

/* ────────────────────── Small presentational bits ────────────────────── */
const isBlank = (v) => v === undefined || v === null || v === "";

const fmtDate = (v) => {
  if (isBlank(v)) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const Field = ({ label, value, mono = false, wide = false, children }) => (
  <div className={`pv-field${wide ? " pv-field-wide" : ""}`}>
    <div className="pv-label">{label}</div>
    <div className={`pv-value${mono ? " pv-mono" : ""}`}>
      {children ?? (isBlank(value) ? "—" : String(value))}
    </div>
  </div>
);

const StatusPill = ({ active }) =>
  active ? (
    <Badge bg="success" pill>
      Active
    </Badge>
  ) : (
    <Badge bg="secondary" pill>
      Inactive
    </Badge>
  );

const EmptyState = ({
  title = "Nothing here yet",
  subtitle = "Try adjusting your filters.",
}) => (
  <div className="text-center text-muted py-5">
    <div className="fw-semibold">{title}</div>
    <div className="small">{subtitle}</div>
  </div>
);

/* ───────────────────────────── Main ───────────────────────────── */
export default function PromptManagement() {
  const [loading, setLoading] = useState(true);
  const [allPrompts, setAllPrompts] = useState([]);
  const [error, setError] = useState(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [initialTestData, setInitialTestData] = useState({
    prompt_text: "",
    model: "",
    parameters: {},
  });
  // tabs (sources)
  const sources = useMemo(() => {
    const s = new Set();
    allPrompts.forEach((p) => p.source && s.add(p.source));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [allPrompts]);
  const [activeSource, setActiveSource] = useState(null);

  // search
  const [search, setSearch] = useState("");

  // compare modal
  const [showCompare, setShowCompare] = useState(false);
  const launchTestModal = useCallback(
    ({ prompt_text = "", model = "", parameters = {} } = {}) => {
      setInitialTestData({ prompt_text, model, parameters });
      setShowTestModal(true);
    },
    []
  );

  // modal state (view/version modal)
  const [showModal, setShowModal] = useState(false);
  const [selectedPromptName, setSelectedPromptName] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);

  // create-new-prompt modal
  const [showCreateModal, setShowCreateModal] = useState(false);

  // misc
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  // create-new-version flow
  const [isCreating, setIsCreating] = useState(false);
  const RESET_OVERRIDES = {
    prompt_text: "",
    description: "",
    persona: "",
    model: "",
    udf1: "",
    udf2: "",
    udf3: "",
  };
  const [newVersionOverrides, setNewVersionOverrides] =
    useState(RESET_OVERRIDES);

  // create-new-prompt form
  const RESET_NEW_PROMPT = {
    name: "",
    prompt_text: "",
    description: "",
    source: "",
    persona: "",
    model: "",
    udf1: "",
    udf2: "",
    udf3: "",
    active: true,
    created_by: "",
    updated_by: "",
  };
  const [newPrompt, setNewPrompt] = useState(RESET_NEW_PROMPT);

  /* ───────────────────────── data load ───────────────────────── */
  const defaultSourceApplied = useRef(false);
  const refreshAll = useCallback(async () => {
    const data = await API().getAll();
    setAllPrompts(data);
    // default the tab only once; later refreshes must not override the user's choice
    if (!defaultSourceApplied.current) {
      defaultSourceApplied.current = true;
      setActiveSource(data.find((p) => p.source)?.source ?? null);
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        await refreshAll();
      } catch (e) {
        setError(e?.message || "Failed to load prompts");
      } finally {
        setLoading(false);
      }
    })();
  }, [refreshAll]);

  /* ───────────────────── transformed / grouped ───────────────────── */
  const grouped = useMemo(() => {
    const map = {};
    allPrompts
      .filter((p) => (activeSource ? p.source === activeSource : true))
      .filter((p) =>
        search.trim()
          ? p.name?.toLowerCase().includes(search.toLowerCase()) ||
            (p.description || "").toLowerCase().includes(search.toLowerCase())
          : true
      )
      .forEach((p) => {
        if (!map[p.name]) map[p.name] = { versions: [] };
        map[p.name].versions.push(p);
      });

    Object.values(map).forEach((bucket) => {
      bucket.versions.sort(
        (a, b) => Number(b.version ?? 0) - Number(a.version ?? 0)
      );
      const activeV = bucket.versions.find((v) => v.active);
      bucket.activeVersion = activeV?.version;
    });
    return map;
  }, [allPrompts, activeSource, search]);

  const activeNames = useMemo(
    () =>
      Object.keys(grouped)
        .filter((name) => grouped[name].activeVersion !== undefined)
        .sort((a, b) => a.localeCompare(b)),
    [grouped]
  );

  // selection
  const selectedBucket = selectedPromptName
    ? grouped[selectedPromptName]
    : undefined;
  const selectedPrompt =
    selectedBucket?.versions.find(
      (v) => Number(v.version) === Number(selectedVersion)
    ) ||
    (selectedBucket?.versions?.[0] ?? undefined);
  const handleEditPromptSave = useCallback(
    async (formValues) => {
      if (!selectedPrompt?.id) return;

      // Build payload expected by PromptInput
      const payload = {
        name: formValues.name,
        prompt_text: formValues.prompt_text,
        description: formValues.description,
        source: formValues.source,
        persona: formValues.persona,
        model: formValues.model,
        created_by: formValues.created_by || selectedPrompt.created_by,
        updated_by: formValues.updated_by || selectedPrompt.updated_by,
        active: selectedPrompt.active, // active handled via Activate button
        udf1: formValues.udf1,
        udf2: formValues.udf2,
        udf3: formValues.udf3,
        // backend update_prompt uses updated_at from PromptInput
        updated_at: new Date().toISOString(),
      };

      try {
        setBusy(true);
        setToast(null);
        await API().update(selectedPrompt.id, payload);
        await refreshAll();
        setToast({ type: "success", msg: "Prompt updated." });
      } catch (e) {
        // rethrow so modal can show its own error if needed
        setToast({ type: "danger", msg: e?.message || "Update failed." });
        throw e;
      } finally {
        setBusy(false);
      }
    },
    [selectedPrompt, refreshAll]
  );

  /* ───────────────────────── handlers ───────────────────────── */
  const openPrompt = useCallback(
    (name) => {
      setSelectedPromptName(name);
      const activeV = grouped[name].activeVersion;
      const initial =
        activeV !== undefined && activeV !== null
          ? Number(activeV)
          : Number(grouped[name].versions?.[0]?.version ?? 0);
      setSelectedVersion(initial);
      setShowModal(true);
      setIsCreating(false);
      setNewVersionOverrides(RESET_OVERRIDES);
    },
    [grouped]
  );

  const handleActivate = useCallback(async () => {
    if (!selectedPromptName || selectedVersion == null) return;
    setBusy(true);
    setToast(null);
    try {
      await API().activate(selectedPromptName, Number(selectedVersion));
      await refreshAll();
      setToast({ type: "success", msg: "Activated successfully." });
    } catch (e) {
      setToast({ type: "danger", msg: e?.message || "Activation failed." });
    } finally {
      setBusy(false);
    }
  }, [selectedPromptName, selectedVersion, refreshAll]);

  const handleSaveNewVersion = useCallback(async () => {
    if (!selectedPrompt) return;
    setBusy(true);
    setToast(null);
    try {
      const payload = {
        name: selectedPrompt.name,
        prompt_text:
          newVersionOverrides.prompt_text || selectedPrompt.prompt_text,
        description:
          newVersionOverrides.description !== ""
            ? newVersionOverrides.description
            : selectedPrompt.description,
        created_by: selectedPrompt.created_by,
        updated_by: selectedPrompt.updated_by,
        active: selectedPrompt.active,
        udf1:
          newVersionOverrides.udf1 !== ""
            ? newVersionOverrides.udf1
            : selectedPrompt.udf1,
        udf2:
          newVersionOverrides.udf2 !== ""
            ? newVersionOverrides.udf2
            : selectedPrompt.udf2,
        udf3:
          newVersionOverrides.udf3 !== ""
            ? newVersionOverrides.udf3
            : selectedPrompt.udf3,
        persona:
          newVersionOverrides.persona !== ""
            ? newVersionOverrides.persona
            : selectedPrompt.persona,
        model:
          newVersionOverrides.model !== ""
            ? newVersionOverrides.model
            : selectedPrompt.model,
        source: selectedPrompt.source,
      };
      await API().create(payload);
      await refreshAll();
      // focus latest
      const latest =
        allPrompts
          .filter((p) => p.name === selectedPrompt.name)
          .sort((a, b) => Number(b.version ?? 0) - Number(a.version ?? 0))[0]
          ?.version ?? null;
      setSelectedVersion(latest !== null ? Number(latest) : null);
      setToast({ type: "success", msg: "New version created." });
      setNewVersionOverrides(RESET_OVERRIDES);
      setIsCreating(false);
    } catch (e) {
      setToast({ type: "danger", msg: e?.message || "Create failed." });
    } finally {
      setBusy(false);
    }
  }, [newVersionOverrides, selectedPrompt, allPrompts, refreshAll]);

  const handleDeleteVersion = useCallback(async () => {
    if (!selectedPrompt?.id) return;
    const activeWarning = selectedPrompt.active
      ? "\n\n⚠️ This version is currently ACTIVE. Deleting it may leave this prompt without an active version until you activate another one."
      : "";
    const ok = window.confirm(
      `Delete this prompt version?\n\nName: ${selectedPrompt.name}\nVersion: ${selectedPrompt.version}${activeWarning}`
    );
    if (!ok) return;

    setBusy(true);
    setToast(null);
    try {
      await API().deleteVersion(selectedPrompt.id);
      await refreshAll();

      const remaining = Object.values(
        grouped[selectedPrompt.name]?.versions || []
      );
      if (remaining.length === 0) {
        setShowModal(false);
        setSelectedPromptName(null);
        setSelectedVersion(null);
      } else {
        const bucket = remaining.sort(
          (a, b) => Number(b.version ?? 0) - Number(a.version ?? 0)
        );
        const nextActive =
          bucket.find((v) => v.active)?.version ?? bucket[0].version;
        setSelectedVersion(Number(nextActive));
      }

      setToast({ type: "success", msg: "Version deleted." });
      setIsCreating(false);
      setNewVersionOverrides(RESET_OVERRIDES);
    } catch (e) {
      setToast({ type: "danger", msg: e?.message || "Delete failed." });
    } finally {
      setBusy(false);
    }
  }, [selectedPrompt, grouped, refreshAll]);

  // Create NEW prompt (version 1)
  const canSaveNewPrompt =
    newPrompt.name.trim() && newPrompt.prompt_text.trim();

  const handleCreatePrompt = useCallback(async () => {
    if (!canSaveNewPrompt) return;
    setBusy(true);
    setToast(null);
    try {
      const payload = {
        ...newPrompt,
        // backend sets version automatically; ensure booleans/strings correct
        active: !!newPrompt.active,
      };
      await API().create(payload);
      await refreshAll();

      // Open the newly created prompt modal focused on latest
      setShowCreateModal(false);
      setNewPrompt(RESET_NEW_PROMPT);

      const newName = payload.name;
      const versions = allPrompts
        .filter((p) => p.name === newName)
        .sort((a, b) => Number(b.version ?? 0) - Number(a.version ?? 0));

      // If refresh hasn't updated allPrompts yet in this render frame, open by name directly
      setSelectedPromptName(newName);
      const latest = versions?.[0]?.version ?? 1;
      setSelectedVersion(Number(latest));
      setShowModal(true);

      setToast({ type: "success", msg: "Prompt created." });
    } catch (e) {
      setToast({ type: "danger", msg: e?.message || "Create prompt failed." });
    } finally {
      setBusy(false);
    }
  }, [newPrompt, canSaveNewPrompt, refreshAll, allPrompts]);

  const isSelectedActive = !!selectedPrompt?.active;
  const currentActiveVersion = selectedBucket?.activeVersion;

  /* ─────────────────────────── UI ─────────────────────────── */
  return (
    <div className="p-3">
      {/* Header */}
      <div className="pm-head">
        <div>
          <div className="pm-title">Prompt Management</div>
          <div className="pm-sub">
            Create, version, activate, delete & test prompts
          </div>
        </div>

        <div className="pm-actions">
          {/* Test Prompt global */}
          <Button
            variant="outline-secondary"
            onClick={() =>
              launchTestModal({
                prompt_text: "",
                model: "",
              })
            }
          >
            Test Prompt
          </Button>

          <div className="pm-search">
            <InputGroup>
              <InputGroup.Text>Search</InputGroup.Text>
              <Form.Control
                placeholder="Name or description"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </InputGroup>
          </div>

          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            Create Prompt
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
          <div className="text-muted small mt-2">Loading prompts…</div>
        </div>
      ) : (
        <>
          {/* Source tabs */}
          <Tabs
            activeKey={activeSource || "all"}
            onSelect={(k) => setActiveSource(k === "all" ? null : k)}
            className="pm-sources"
          >
            <Tab eventKey="all" title="All Sources" />
            {sources.map((s) => (
              <Tab eventKey={s} title={s} key={s} />
            ))}
          </Tabs>

          {/* Active prompts list */}
          <div className="pm-list-head">
            <div className="pm-list-title">Active Prompts</div>
            <div className="pm-count">
              {activeNames.length} {activeNames.length === 1 ? "item" : "items"}
            </div>
          </div>

          {activeNames.length === 0 ? (
            <EmptyState
              title="No active prompts"
              subtitle="Switch source or clear search to see more."
            />
          ) : (
            <Table responsive hover className="pm-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th className="text-center" style={{ width: 140 }}>
                    Version
                  </th>
                  <th style={{ width: 100 }} aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {activeNames.map((n) => (
                  <tr key={n}>
                    <td className="text-break align-middle pm-name">{n}</td>
                    <td className="text-center align-middle">
                      <Badge bg="success" pill>
                        v{Number(grouped[n].activeVersion)}
                      </Badge>
                    </td>
                    <td className="text-end align-middle">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="pm-view"
                        onClick={() => openPrompt(n)}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          {/* VIEW / VERSION MODAL */}
          <Modal
            show={showModal}
            onHide={() => {
              setShowModal(false);
              setIsCreating(false);
              setNewVersionOverrides(RESET_OVERRIDES);
              setShowCompare(false); // important
            }}
            size="xl"
            centered
            scrollable
          >
            <Modal.Header closeButton className="pv-header">
              <div className="w-100">
                <div className="pv-title-row">
                  <div className="pv-title-block">
                    <div className="pv-eyebrow">Prompt</div>
                    <div className="pv-title text-break">
                      {selectedPromptName || "Prompt"}
                    </div>
                    <div className="pv-active-line">
                      <span>Active version</span>
                      {currentActiveVersion !== undefined ? (
                        <Badge bg="success" pill>
                          v{Number(currentActiveVersion)}
                        </Badge>
                      ) : (
                        <Badge bg="secondary" pill>
                          None
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>

                <div className="pv-toolbar">
                  <Button
                    size="sm"
                    variant={showCompare ? "secondary" : "outline-secondary"}
                    onClick={() => setShowCompare(true)}
                    disabled={
                      !selectedBucket?.versions ||
                      selectedBucket.versions.length < 2
                    }
                  >
                    Compare Versions
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() =>
                      launchTestModal({
                        prompt_text: selectedPrompt?.prompt_text || "",
                        model: selectedPrompt?.model || "",
                      })
                    }
                    disabled={!selectedPrompt}
                  >
                    Test This Version
                  </Button>
                  <Button
                    size="sm"
                    variant={isSelectedActive ? "outline-secondary" : "success"}
                    onClick={handleActivate}
                    disabled={
                      !selectedPromptName ||
                      selectedVersion == null ||
                      isSelectedActive ||
                      busy
                    }
                  >
                    {isSelectedActive
                      ? "Already Active"
                      : busy
                      ? "Activating..."
                      : "Activate"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    className="pv-toolbar-end"
                    onClick={handleDeleteVersion}
                    disabled={!selectedPrompt?.id || busy}
                  >
                    {busy ? "Deleting…" : "Delete"}
                  </Button>
                </div>

                {toast && (
                  <Alert
                    variant={toast.type}
                    className="mt-3 mb-0 py-2"
                    onClose={() => setToast(null)}
                    dismissible
                  >
                    {toast.msg}
                  </Alert>
                )}
              </div>
            </Modal.Header>

            <Modal.Body className="pt-3">
              <Row>
                {/* Versions list */}
                <Col md={3} className="mb-4">
                  <div className="text-muted fw-semibold mb-2">Versions</div>
                  {selectedBucket?.versions?.length ? (
                    <div className="pv-versions">
                      {selectedBucket.versions.map((v) => {
                        const selected =
                          Number(selectedVersion) === Number(v.version);
                        return (
                          <button
                            type="button"
                            key={`${v.name}-${v.version}`}
                            className={`pv-version${selected ? " is-selected" : ""}`}
                            onClick={() => setSelectedVersion(Number(v.version))}
                            aria-pressed={selected}
                          >
                            <span className="pv-version-top">
                              <span className="pv-version-num">
                                v{Number(v.version)}
                              </span>
                              <StatusPill active={!!v.active} />
                            </span>
                            <span className="pv-version-date">
                              {fmtDate(v.updated_at || v.created_at)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <EmptyState
                      title="No versions"
                      subtitle="Create a new version to get started."
                    />
                  )}
                </Col>

                {/* Version details */}
                <Col md={9}>
                  <div className="text-muted fw-semibold mb-2">
                    Version Details
                  </div>

                  {!selectedPrompt ? (
                    <EmptyState title="Select a version" />
                  ) : (
                    <>
                      <div className="pv-section-title">Prompt Text</div>
                      <pre className="pv-prompt">
                        {selectedPrompt.prompt_text || "—"}
                      </pre>

                      <div className="pv-section-title">Configuration</div>
                      <div className="pv-grid">
                        <Field label="Model" value={selectedPrompt.model} mono />
                        <Field label="Source" value={selectedPrompt.source} />
                        <Field label="Persona" value={selectedPrompt.persona} />
                        <Field label="Status">
                          <StatusPill active={!!selectedPrompt.active} />
                        </Field>
                        <Field
                          label="Description"
                          value={selectedPrompt.description}
                          wide
                        />
                        <Field label="UDF1" value={selectedPrompt.udf1} mono />
                        <Field label="UDF2" value={selectedPrompt.udf2} mono />
                        <Field label="UDF3" value={selectedPrompt.udf3} mono />
                      </div>

                      <div className="pv-section-title">Audit</div>
                      <div className="pv-grid">
                        <Field label="Created By" value={selectedPrompt.created_by} />
                        <Field label="Created At" value={fmtDate(selectedPrompt.created_at)} />
                        <Field label="Updated By" value={selectedPrompt.updated_by} />
                        <Field label="Updated At" value={fmtDate(selectedPrompt.updated_at)} />
                        <Field label="Version ID" value={selectedPrompt.id} mono wide />
                      </div>

                      {/* Bottom action area */}
                      {!isCreating ? (
                        <div className="pv-actions">
                          <div className="d-flex justify-content-end gap-2">
                            <Button
                              variant="outline-secondary"
                              onClick={() => setShowEditModal(true)}
                              disabled={!selectedPrompt || busy}
                            >
                              Edit This Version
                            </Button>

                            <Button
                              variant="primary"
                              onClick={() => {
                                setIsCreating(true);
                                setNewVersionOverrides(RESET_OVERRIDES);
                              }}
                              disabled={!selectedPromptName || busy}
                            >
                              Create New Version
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="mt-4 rounded border p-3">
                            <div className="text-muted fw-semibold mb-3">
                              New Version Overrides (optional)
                            </div>
                            <Row className="g-3">
                              <Col md={12}>
                                <FloatingLabel label="Prompt Text Override">
                                  <Form.Control
                                    as="textarea"
                                    placeholder="Leave empty to copy current prompt_text"
                                    style={{ minHeight: 120 }}
                                    value={newVersionOverrides.prompt_text}
                                    onChange={(e) =>
                                      setNewVersionOverrides((p) => ({
                                        ...p,
                                        prompt_text: e.target.value,
                                      }))
                                    }
                                  />
                                </FloatingLabel>
                              </Col>
                              <Col md={6}>
                                <FloatingLabel label="Description Override">
                                  <Form.Control
                                    type="text"
                                    value={newVersionOverrides.description}
                                    onChange={(e) =>
                                      setNewVersionOverrides((p) => ({
                                        ...p,
                                        description: e.target.value,
                                      }))
                                    }
                                  />
                                </FloatingLabel>
                              </Col>
                              <Col md={6}>
                                <FloatingLabel label="Persona Override">
                                  <Form.Control
                                    type="text"
                                    value={newVersionOverrides.persona}
                                    onChange={(e) =>
                                      setNewVersionOverrides((p) => ({
                                        ...p,
                                        persona: e.target.value,
                                      }))
                                    }
                                  />
                                </FloatingLabel>
                              </Col>
                              <Col md={6}>
                                <FloatingLabel label="Model Override">
                                  <Form.Control
                                    type="text"
                                    value={newVersionOverrides.model}
                                    onChange={(e) =>
                                      setNewVersionOverrides((p) => ({
                                        ...p,
                                        model: e.target.value,
                                      }))
                                    }
                                  />
                                </FloatingLabel>
                              </Col>
                              <Col md={6}>
                                <FloatingLabel label="UDF1 Override">
                                  <Form.Control
                                    type="text"
                                    value={newVersionOverrides.udf1}
                                    onChange={(e) =>
                                      setNewVersionOverrides((p) => ({
                                        ...p,
                                        udf1: e.target.value,
                                      }))
                                    }
                                  />
                                </FloatingLabel>
                              </Col>
                              <Col md={6}>
                                <FloatingLabel label="UDF2 Override">
                                  <Form.Control
                                    type="text"
                                    value={newVersionOverrides.udf2}
                                    onChange={(e) =>
                                      setNewVersionOverrides((p) => ({
                                        ...p,
                                        udf2: e.target.value,
                                      }))
                                    }
                                  />
                                </FloatingLabel>
                              </Col>
                              <Col md={6}>
                                <FloatingLabel label="UDF3 Override">
                                  <Form.Control
                                    type="text"
                                    value={newVersionOverrides.udf3}
                                    onChange={(e) =>
                                      setNewVersionOverrides((p) => ({
                                        ...p,
                                        udf3: e.target.value,
                                      }))
                                    }
                                  />
                                </FloatingLabel>
                              </Col>
                            </Row>
                          </div>

                          <div className="d-flex justify-content-end flex-wrap gap-2 mt-3">
                            <Button
                              variant="outline-primary"
                              onClick={() => {
                                const effectivePromptText =
                                  newVersionOverrides.prompt_text ||
                                  selectedPrompt?.prompt_text ||
                                  "";
                                const effectiveModel =
                                  newVersionOverrides.model ||
                                  selectedPrompt?.model ||
                                  "";
                                launchTestModal({
                                  prompt_text: effectivePromptText,
                                  model: effectiveModel,
                                });
                              }}
                              disabled={busy}
                            >
                              Test With Overrides
                            </Button>

                            <Button
                              variant="outline-secondary"
                              onClick={() => {
                                setIsCreating(false);
                                setNewVersionOverrides(RESET_OVERRIDES);
                              }}
                              disabled={busy}
                            >
                              Cancel
                            </Button>

                            <Button
                              variant="primary"
                              onClick={handleSaveNewVersion}
                              disabled={!selectedPromptName || busy}
                            >
                              {busy ? "Saving..." : "Save"}
                            </Button>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </Col>
              </Row>
            </Modal.Body>
          </Modal>

          {/* CREATE NEW PROMPT MODAL */}
          <Modal
            show={showCreateModal}
            onHide={() => {
              setShowCreateModal(false);
              setNewPrompt(RESET_NEW_PROMPT);
            }}
            size="lg"
            centered
            scrollable
          >
            <Modal.Header closeButton>
              <Modal.Title>Create New Prompt</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {toast && (
                <Alert
                  variant={toast.type}
                  className="mb-3 py-2"
                  onClose={() => setToast(null)}
                  dismissible
                >
                  {toast.msg}
                </Alert>
              )}

              <Row className="g-3">
                <Col md={6}>
                  <FloatingLabel label="Name*">
                    <Form.Control
                      type="text"
                      value={newPrompt.name}
                      onChange={(e) =>
                        setNewPrompt((p) => ({ ...p, name: e.target.value }))
                      }
                      placeholder="Unique prompt name"
                    />
                  </FloatingLabel>
                </Col>
                <Col md={6}>
                  <FloatingLabel label="Source">
                    {/* allow free text but help via datalist of known sources */}
                    <Form.Control
                      list="known-sources"
                      value={newPrompt.source}
                      onChange={(e) =>
                        setNewPrompt((p) => ({ ...p, source: e.target.value }))
                      }
                      placeholder="e.g., fxgpt, emgpt"
                    />
                    <datalist id="known-sources">
                      {sources.map((s) => (
                        <option key={s} value={s} />
                      ))}
                    </datalist>
                  </FloatingLabel>
                </Col>
                <Col md={12}>
                  <FloatingLabel label="Prompt Text*">
                    <Form.Control
                      as="textarea"
                      style={{ minHeight: 140 }}
                      value={newPrompt.prompt_text}
                      onChange={(e) =>
                        setNewPrompt((p) => ({
                          ...p,
                          prompt_text: e.target.value,
                        }))
                      }
                      placeholder="System/assistant prompt text"
                    />
                  </FloatingLabel>
                </Col>
                <Col md={12}>
                  <FloatingLabel label="Description">
                    <Form.Control
                      type="text"
                      value={newPrompt.description}
                      onChange={(e) =>
                        setNewPrompt((p) => ({
                          ...p,
                          description: e.target.value,
                        }))
                      }
                      placeholder="Short summary"
                    />
                  </FloatingLabel>
                </Col>
                <Col md={4}>
                  <FloatingLabel label="Persona">
                    <Form.Control
                      type="text"
                      value={newPrompt.persona}
                      onChange={(e) =>
                        setNewPrompt((p) => ({ ...p, persona: e.target.value }))
                      }
                      placeholder="e.g., support-agent"
                    />
                  </FloatingLabel>
                </Col>
                <Col md={4}>
                  <FloatingLabel label="Model">
                    <Form.Control
                      type="text"
                      value={newPrompt.model}
                      onChange={(e) =>
                        setNewPrompt((p) => ({ ...p, model: e.target.value }))
                      }
                      placeholder="e.g., GPT-5"
                    />
                  </FloatingLabel>
                </Col>
                <Col md={4}>
                  <FloatingLabel label="Active">
                    <Form.Select
                      value={String(newPrompt.active)}
                      onChange={(e) =>
                        setNewPrompt((p) => ({
                          ...p,
                          active: e.target.value === "true",
                        }))
                      }
                    >
                      <option value="true">True</option>
                      <option value="false">False</option>
                    </Form.Select>
                  </FloatingLabel>
                </Col>
                <Col md={4}>
                  <FloatingLabel label="UDF1">
                    <Form.Control
                      type="text"
                      value={newPrompt.udf1}
                      onChange={(e) =>
                        setNewPrompt((p) => ({ ...p, udf1: e.target.value }))
                      }
                    />
                  </FloatingLabel>
                </Col>
                <Col md={4}>
                  <FloatingLabel label="UDF2">
                    <Form.Control
                      type="text"
                      value={newPrompt.udf2}
                      onChange={(e) =>
                        setNewPrompt((p) => ({ ...p, udf2: e.target.value }))
                      }
                    />
                  </FloatingLabel>
                </Col>
                <Col md={4}>
                  <FloatingLabel label="UDF3">
                    <Form.Control
                      type="text"
                      value={newPrompt.udf3}
                      onChange={(e) =>
                        setNewPrompt((p) => ({ ...p, udf3: e.target.value }))
                      }
                    />
                  </FloatingLabel>
                </Col>
                <Col md={6}>
                  <FloatingLabel label="Created By">
                    <Form.Control
                      type="text"
                      value={newPrompt.created_by}
                      onChange={(e) =>
                        setNewPrompt((p) => ({
                          ...p,
                          created_by: e.target.value,
                        }))
                      }
                    />
                  </FloatingLabel>
                </Col>
                <Col md={6}>
                  <FloatingLabel label="Updated By">
                    <Form.Control
                      type="text"
                      value={newPrompt.updated_by}
                      onChange={(e) =>
                        setNewPrompt((p) => ({
                          ...p,
                          updated_by: e.target.value,
                        }))
                      }
                    />
                  </FloatingLabel>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer className="border-0">
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setShowCreateModal(false);
                  setNewPrompt(RESET_NEW_PROMPT);
                }}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleCreatePrompt}
                disabled={!canSaveNewPrompt || busy}
              >
                {busy ? "Saving…" : "Create"}
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
      <TestPromptModal
        show={showTestModal}
        onHide={() => setShowTestModal(false)}
        initialData={initialTestData}
      />
      <ComparePromptVersions
        show={showCompare}
        onHide={() => setShowCompare(false)}
        versions={selectedBucket?.versions || []}
      />
      <EditPromptModal
  show={showEditModal}
  onHide={() => setShowEditModal(false)}
  prompt={selectedPrompt}
  onSave={handleEditPromptSave}
  sources={sources}
/>
    </div>
  );
}
